/**
 * Reads a creator's plan and revenue usage, and decides whether their sales
 * pages should still be able to take payments.
 *
 * Revenue comes from `creator_analytics.total_revenue`, which Postgres
 * triggers already maintain. The plan lives on `profiles` using the owner's
 * real columns:
 *
 *   profiles.plan_type          ('free' | 'pro' | 'lifetime')
 *   profiles.plan_expiry_date   (pro counts only while this is in the future)
 *   profiles.usdc_polygon_wallet (sender wallet, shown in upgrade proof)
 *
 * The public sales page is unauthenticated, so we prefer a security-definer
 * RPC (`get_creator_plan_status`) that exposes only these fields. See
 * PRO_PLAN_SCHEMA.md. If that RPC is missing we fall back to direct reads,
 * which only work for signed-in owners.
 */

import { supabase } from '../supabase';
import {
  FREE_REVENUE_LIMIT,
  FAIL_OPEN_WHEN_UNKNOWN,
  type PlanId,
} from '../config/plans';

export interface PlanStatus {
  plan: PlanId;
  planExpiresAt: string | null;
  totalRevenue: number;
  /** null = unlimited */
  limit: number | null;
  /** null = unlimited */
  remaining: number | null;
  /** 0-100, against the cap */
  percentUsed: number;
  /** Pro or Lifetime, and not expired */
  isPro: boolean;
  /** Free plan that has hit the cap: checkout must be hidden */
  isLimitReached: boolean;
  /** false when the status could not be read at all */
  known: boolean;
}

const toPlanId = (value: unknown): PlanId => {
  if (value === 'pro' || value === 'lifetime') return value;
  return 'free';
};

/** A paid plan is only active while its expiry is in the future. */
export const isPaidPlanActive = (
  plan: PlanId,
  planExpiresAt: string | null
): boolean => {
  if (plan === 'free') return false;
  if (!planExpiresAt) return true;
  return new Date(planExpiresAt).getTime() > Date.now();
};

export const computePlanStatus = (input: {
  plan?: unknown;
  planExpiresAt?: string | null;
  totalRevenue?: number | null;
  known?: boolean;
}): PlanStatus => {
  const known = input.known !== false;
  const plan = toPlanId(input.plan);
  const planExpiresAt = input.planExpiresAt ?? null;
  const totalRevenue = Number(input.totalRevenue ?? 0) || 0;
  const isPro = known && isPaidPlanActive(plan, planExpiresAt);

  const limit = isPro ? null : FREE_REVENUE_LIMIT;
  const isLimitReached = known && !isPro && totalRevenue >= FREE_REVENUE_LIMIT;

  return {
    plan,
    planExpiresAt,
    totalRevenue,
    limit,
    remaining: isLimitReached ? 0 : limit === null ? null : Math.max(0, limit - totalRevenue),
    percentUsed: limit === null ? 0 : Math.min(100, (totalRevenue / limit) * 100),
    isPro,
    isLimitReached,
    known,
  };
};

const normalise = (row: any): PlanStatus =>
  computePlanStatus({
    plan: row?.plan ?? row?.plan_type,
    planExpiresAt: row?.plan_expires_at ?? row?.plan_expiry_date ?? null,
    totalRevenue: row?.total_revenue,
    known: true,
  });

/**
 * Unreachable status, used when the quota lookup failed entirely.
 *
 * FAIL_OPEN_WHEN_UNKNOWN (default) lets the sale through rather than breaking
 * sales pages for every creator before the RPC is deployed. Set it to false to
 * fail closed instead, which hides checkout for everyone until the status can
 * actually be read.
 */
const unknownStatus = (): PlanStatus =>
  FAIL_OPEN_WHEN_UNKNOWN
    ? computePlanStatus({ plan: 'free', totalRevenue: 0, known: false })
    : computePlanStatus({ plan: 'free', totalRevenue: FREE_REVENUE_LIMIT, known: true });

/**
 * Fetch the plan status for a creator. Never throws: on failure it returns a
 * status flagged `known: false` so callers can decide what to do.
 */
export const fetchCreatorPlanStatus = async (
  creatorId: string | null | undefined
): Promise<PlanStatus> => {
  if (!creatorId) return unknownStatus();

  // Preferred path: works for anonymous visitors too.
  try {
    const { data, error } = await supabase.rpc('get_creator_plan_status', {
      p_creator_id: creatorId,
    });

    if (!error) {
      const row = Array.isArray(data) ? data[0] : data;
      if (row) return normalise(row);
    }
  } catch {
    // RPC not deployed yet, fall through.
  }

  // Fallback: direct reads. Fine on the creator's own dashboard, blocked by
  // RLS on the public sales page. Uses the owner's real profile columns.
  try {
    const [profileRes, analyticsRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('plan_type, plan_expiry_date')
        .eq('id', creatorId)
        .maybeSingle(),
      supabase
        .from('creator_analytics')
        .select('total_revenue')
        .eq('creator_id', creatorId)
        .maybeSingle(),
    ]);

    if (profileRes.error || analyticsRes.error) return unknownStatus();

    return normalise({
      plan_type: profileRes.data?.plan_type,
      plan_expiry_date: profileRes.data?.plan_expiry_date ?? null,
      total_revenue: analyticsRes.data?.total_revenue ?? 0,
    });
  } catch {
    return unknownStatus();
  }
};

/** Status for the signed-in creator, used by the dashboard and pricing page. */
export const fetchMyPlanStatus = async (): Promise<PlanStatus> => {
  const { data } = await supabase.auth.getUser();
  if (!data.user) return unknownStatus();
  return fetchCreatorPlanStatus(data.user.id);
};

export const money = (value: number): string =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;